import { useEffect, useState } from 'react';

import { useBookings } from '@/api/bookings';
import { useItemUnits } from '@/api/masters';
import { useDebounced } from '@/components/useDebounced';
import { referenceLabel } from '@core/bookings/reference';
import { readScan, type LookupKind } from '@core/scan';
import { can, hasFeature } from '@core/session/capability';
import type { Session } from '@core/session/types';
import { withReference } from '@core/units/referenceNumber';

/**
 * Find anything by its code, from anywhere in the product.
 *
 * <p>
 * <strong>The two things a shop points a scanner at.</strong> A customer copy carries a QR of the
 * booking reference; a tailor's docket carries one of the unit serial. Both have printed correctly
 * since the day they were written and the product had nowhere to read one — the header's magnifier
 * was a disabled button captioned "Search arrives with the catalogue".
 * </p>
 *
 * <p>
 * <strong>The layout is the template's own search modal, not an invention.</strong> Dreams ERP
 * ships one in `components/common-header/commonHeader.tsx`: a titled header with a close control, a
 * framed input with the magnifier trailing, a row of scope chips under "I'm looking for", and
 * results as iconed rows under bold section headings. That markup is followed here, because the
 * house rule is to match the template before inventing a layout — only its stylesheet was vendored,
 * so its own views are the reference for markup the CSS alone does not carry.
 * </p>
 *
 * <p>
 * <strong>Two deliberate departures.</strong> The template's input is `h-8`; 32px misses the 44px
 * touch target a counter tablet is built around, which is the argument `shell/textScale.ts` exists
 * to settle, so the height comes off the scale instead. And the template's "Quick Actions" are
 * creates it has routes for; ours are jumps, because there is no router — three of the register's
 * <em>saved views</em>, which are a day's work already filtered, and the two money screens, which
 * are plain destinations kept here for how often a counter wants them. See `shortcuts` below for
 * why that second kind was allowed in after the first rule excluded it.
 * </p>
 *
 * <p>
 * <strong>It asks both indexes and lets the answers decide</strong>, which is `scan.ts`'s rule
 * rather than this component's: a numbering series can be configured into any shape, so a
 * classifier reading the text would be right on the seed data and wrong on a real shop.
 * </p>
 *
 * <p>
 * <strong>Each lookup is gated on the permission that screen needs.</strong> A wardrobe assistant
 * with no `RentalOrder:View` gets the garment half and no bookings — rather than a list of results
 * that 403 on arrival, which is how a search teaches people it is lying.
 * </p>
 */
export function GlobalFind({
  session,
  onOpen,
  onClose,
}: {
  session: Session;
  /**
   * Navigates, carrying the code so the destination opens with it in its own search box — or a
   * saved view, for the shortcuts, which carry no code at all.
   */
  onOpen: (key: string, find: string, view?: string, record?: number) => void;
  onClose: () => void;
}) {
  const [text, setText] = useState('');

  /**
   * The term Enter was pressed on, kept until its answer arrives.
   *
   * <p>
   * A hardware scanner types the code and presses Enter in the same instant, long before the
   * debounce lets the search go — so an Enter that only looked at the results on screen did nothing
   * on a scan, and the clerk had to click. Held here and settled by the effect below once the lookup
   * for exactly this term has answered.
   * </p>
   */
  const [enterFor, setEnterFor] = useState<string | null>(null);

  /**
   * Which index the person has narrowed to, or both.
   *
   * <p>
   * The template's "I'm looking for" is a row of links to sections. Here the same control does real
   * work: it drops one of the two lookups, which is what a clerk holding a garment tag wants when a
   * serial happens to also match something in a booking.
   * </p>
   */
  const [scope, setScope] = useState<LookupKind | null>(null);

  const intent = readScan(useDebounced(text));

  const branchId = session.branch?.id;

  const maySee: Record<LookupKind, boolean> = {
    booking: can(session, 'RentalOrder:View'),
    unit: can(session, 'ItemUnit:View'),
  };

  const wants = (kind: LookupKind) =>
    maySee[kind] &&
    (scope === null || scope === kind) &&
    intent.lookups.some((lookup) => lookup.kind === kind)
      ? intent.term
      : '';

  // `pageSize: 5` — this is a find, not a list. A longer answer means the term was too broad, and
  // the screens behind it are where a long answer belongs.
  const bookings = useBookings(wants('booking') ? branchId : undefined, {
    search: wants('booking') || undefined,
    page: 1,
    pageSize: 5,
  });

  const units = useItemUnits(wants('unit') ? branchId : undefined, {
    search: wants('unit') || undefined,
    page: 1,
    pageSize: 5,
  });

  const bookingHits = wants('booking') ? (bookings.data?.items ?? []) : [];
  const unitHits = wants('unit') ? (units.data?.items ?? []) : [];
  const searching = bookings.isFetching || units.isFetching;
  const asked = intent.lookups.length > 0;
  const settled =
    !searching && (!wants('booking') || bookings.isSuccess) && (!wants('unit') || units.isSuccess);

  // One answer alone is the scanner's whole interaction: point, beep, arrive — on the booking
  // itself, with its garments and their photographs, rather than on a list of one.
  const onlyBooking = bookingHits.length === 1 && unitHits.length === 0 ? bookingHits[0] : null;
  const onlyUnit = unitHits.length === 1 && bookingHits.length === 0;

  const open = (key: string, record?: number) => {
    onOpen(key, intent.term, undefined, record);
    onClose();
  };

  useEffect(() => {
    if (enterFor === null || enterFor !== intent.term || !settled) {
      return;
    }

    setEnterFor(null);

    if (onlyBooking) {
      onOpen('bookings', intent.term, undefined, onlyBooking.id);
      onClose();
    } else if (onlyUnit) {
      onOpen('units', intent.term);
      onClose();
    }
  }, [enterFor, intent.term, settled, onlyBooking, onlyUnit, onOpen, onClose]);

  /**
   * Where a counter jumps to without typing anything. Shown only before a search, because once
   * there are results the results are the answer.
   *
   * <p>
   * <strong>Three saved views and the two money screens</strong>, and the mix is deliberate. The
   * first three are a day's work already filtered — one click deeper than the menu goes — and the
   * argument for them was that a link to a list the sidebar already offers is not an action. Cash
   * and Expenses are that link, and they earn it anyway: they sit two levels down a Commerce group
   * that is collapsed until something inside it is open, they are what a clerk reaches for with a
   * receipt already in hand, and the magnifier that opens this panel is in the header on every
   * screen — one click, against expanding a group and picking a row. Being reachable elsewhere is
   * not a reason to withhold the shorter path.
   * </p>
   *
   * <p>
   * <strong>Each carries its own key and its own grant.</strong> The list was three booking views
   * with the target hardcoded to `bookings`; a shortcut now names the screen it opens, so adding
   * one outside the register does not need a second branch at the call site. `cash` asks for
   * `ViewFinancials` rather than `View` — it is one of the two places that action is enforced —
   * and both money screens need the plan to carry `CashAndExpense`, which is the second axis the
   * menu applies and a search that ignored it would offer a locked screen as a shortcut.
   * </p>
   */
  const shortcuts: { key: string; view?: string; label: string; icon: string }[] = [
    ...(maySee.booking
      ? [
          {
            key: 'bookings',
            view: 'pickups',
            label: "Today's pickups",
            icon: 'icon icon-package-check',
          },
          {
            key: 'bookings',
            view: 'returns',
            label: "Today's returns",
            icon: 'icon icon-package-open',
          },
          { key: 'bookings', view: 'overdue', label: 'Overdue', icon: 'icon icon-alarm-clock' },
        ]
      : []),
    ...(can(session, 'CashAndBank:ViewFinancials') && hasFeature(session, 'CashAndExpense')
      ? [{ key: 'cash', label: 'Cash & bank', icon: 'icon icon-wallet' }]
      : []),
    ...(can(session, 'Expense:View') && hasFeature(session, 'CashAndExpense')
      ? [{ key: 'expenses', label: 'Expenses', icon: 'icon icon-receipt' }]
      : []),
  ];

  return (
    /*
      `z-9999` is the app-overlay rung — see the ladder in `style.css`. `data-shortcut-layer` takes
      the whole shortcut vocabulary while this is open, so Alt+P here does not print the page behind
      it.
    */
    <div
      className="fixed inset-0 z-9999 overflow-y-auto bg-[var(--overlay-scrim)]"
      data-shortcut-layer
      role="dialog"
      aria-modal="true"
      aria-label="Search"
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <div className="flex min-h-full items-start justify-center px-4 py-[10vh]">
        <div className="w-full max-w-[630px] rounded-lg border border-[var(--overlay-border)] bg-[var(--overlay-surface)] p-6 shadow-lg">
          <div className="mb-5 flex items-center justify-between">
            <p className="text-title text-lg font-bold">Search</p>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="border-border-color text-title hover:bg-danger hover:border-danger inline-flex size-8 cursor-pointer items-center justify-center rounded-md border bg-white text-base hover:text-white"
            >
              <i className="icon icon-x" />
            </button>
          </div>

          {/*
            A `<form>`, because a hardware scanner ends its payload with Enter and that keystroke has
            to land somewhere that does not reload the page. The box is the only control in it.
          */}
          <form
            className="relative mb-5"
            onSubmit={(event) => {
              event.preventDefault();
              setEnterFor(readScan(text).term);
            }}
          >
            <input
              // The one place in the product where a screen may focus itself on a touch device: it
              // opens only because somebody asked for it, and it is a search box.
              autoFocus
              value={text}
              onChange={(event) => {
                setText(event.target.value);
                setEnterFor(null);
              }}
              onKeyDown={(event) => event.key === 'Escape' && onClose()}
              placeholder="Scan a tag, or type a booking number, name or serial"
              aria-label="Search"
              autoComplete="off"
              className="border-border-color text-title focus:border-primary h-11 w-full rounded-md border bg-white ps-3 pe-11 text-[14px] shadow focus:ring-0"
            />
            <span className="text-default pointer-events-none absolute end-3 top-1/2 -translate-y-1/2">
              <i
                className={searching ? 'icon icon-loader-circle animate-spin' : 'icon icon-search'}
              />
            </span>
          </form>

          {/* Offered only to somebody holding both grants — one chip is not a choice. */}
          {maySee.booking && maySee.unit && (
            <div className="mb-5">
              <p className="text-title mb-2 font-semibold">I’m looking for</p>
              <div className="flex flex-wrap items-center gap-3">
                <Chip
                  label="Bookings"
                  on={scope === 'booking'}
                  onPick={() => setScope((was) => (was === 'booking' ? null : 'booking'))}
                />
                <Chip
                  label="Garments"
                  on={scope === 'unit'}
                  onPick={() => setScope((was) => (was === 'unit' ? null : 'unit'))}
                />
              </div>
            </div>
          )}

          {shortcuts.length > 0 && !asked && (
            <div className="mb-5">
              <p className="text-title mb-2 font-semibold">Quick actions</p>
              <div className="space-y-1">
                {shortcuts.map((item) => (
                  // Keyed on both, because two shortcuts can share a screen — the three register
                  // views do — and `key` has to be unique across the list rather than within a
                  // target.
                  <Row
                    key={`${item.key}:${item.view ?? ''}`}
                    icon={item.icon}
                    title={item.label}
                    onOpen={() => {
                      onOpen(item.key, '', item.view);
                      onClose();
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {!asked ? (
            <p className="text-default text-[13px]">
              Point the scanner at a customer copy or a garment tag, or type at least three
              characters.
            </p>
          ) : bookingHits.length === 0 && unitHits.length === 0 ? (
            <p className="text-default text-[13px]">
              {searching ? 'Looking…' : `Nothing matches “${intent.term}”.`}
            </p>
          ) : (
            <div className="max-h-[42vh] overflow-y-auto">
              {bookingHits.length > 0 && (
                <div className="mb-5">
                  <p className="text-title mb-2 font-semibold">Bookings</p>
                  <div className="space-y-1">
                    {bookingHits.map((booking) => (
                      <Row
                        key={booking.id}
                        icon="icon icon-clipboard-list"
                        title={referenceLabel(booking)}
                        detail={`${booking.customerName} · ${booking.customerMobile}`}
                        onOpen={() => open('bookings', booking.id)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {unitHits.length > 0 && (
                <div>
                  <p className="text-title mb-2 font-semibold">Garments</p>
                  <div className="space-y-1">
                    {unitHits.map((unit) => (
                      <Row
                        key={unit.id}
                        icon="icon icon-shirt"
                        title={withReference(unit.serialCode, unit.referenceNumber)}
                        detail={`${unit.itemName} · ${unit.variantDisplayName}`}
                        onOpen={() => open('units')}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** A scope filter, in the template's chip shape. */
function Chip({ label, on, onPick }: { label: string; on: boolean; onPick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onPick}
      className={`cursor-pointer rounded-md border px-2 py-1 text-xs font-medium ${
        on
          ? 'border-primary bg-primary text-white'
          : 'bg-light-transparent border-border-color text-title hover:border-primary hover:bg-primary hover:text-white'
      }`}
    >
      {label}
    </button>
  );
}

/** One row, in the template's shape: a muted leading glyph that inks on hover, then the record. */
function Row({
  icon,
  title,
  detail,
  onOpen,
}: {
  icon: string;
  title: string;
  detail?: string;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="text-title hover:bg-light group flex w-full cursor-pointer items-center gap-2 rounded-md px-[10px] py-[5px] text-start"
    >
      <i className={`${icon} text-default group-hover:text-title shrink-0 text-base`} />
      <span className="min-w-0">
        <span className="font-mono-nums block truncate text-[13px]">{title}</span>
        {detail && <span className="text-default block truncate text-[12px]">{detail}</span>}
      </span>
    </button>
  );
}
